/* eslint-disable camelcase */
import { Component, Vue } from 'vue-property-decorator'
import { Nullable } from '@/shared/types/app.types'
import { SampleOperation } from '@/api/operations/models/sample-operation'
import { SAMPLE_STATES } from '@/api/resources/entityState/sample-state'

@Component
export class SampleOperationGuardMixin extends Vue {
    /// GUARD OPERATIONS ///

    $_canCreateSample(): boolean {
        return this.$_hasOperationInCurrentLaboratory(SampleOperation.CREATE)
    }
    $_canEditSample(): boolean {
        return this.$_hasOperationInCurrentLaboratory(SampleOperation.EDIT)
    }
    $_canDeleteSample(): boolean {
        return this.$_hasOperationInCurrentLaboratory(SampleOperation.DELETE)
    }

    $_canProcessSample(): boolean {
        return this.$_canEditSample() && (
            this.$_hasOperationInCurrentLaboratory(SampleOperation.PROCESS) ||
            this.$_hasOperationInCurrentLaboratory(SampleOperation.REPROCESS)
        )
    }
    $_canRequestSampleReprocessing(): boolean {
        return this.$_hasOperationInCurrentLaboratory(SampleOperation.REQUEST_REPROCESS)
    }
    $_canRequestAnySampleRevision(): boolean {
        // add here new sample revision types
        return this.$_canRequestSampleReprocessing()
    }

    $_canAttachDocumentToSample(): boolean {
        return this.$_hasOperationInCurrentLaboratory(SampleOperation.ATTACH)
    }

    $_canDiscardSample(): boolean {
        return this.$_hasOperationInCurrentLaboratory(SampleOperation.DISCARD)
    }

    $_canDisposeSample(sample: any): boolean {
        const isStateOk = [SAMPLE_STATES.PROCESSED.code, SAMPLE_STATES.ARCHIVED.code].includes(sample?.status.code)
        return isStateOk &&
            this.$_canDiscardSample()
    }
    $_canUndoDisposedSample(sample: any): boolean {
        const isStateOk = SAMPLE_STATES.DISCARDED.code === sample?.status.code
        return isStateOk &&
            this.$_canDiscardSample()
    }

    $_canTakeOutFromLaboratory(): boolean {
        return this.$_hasOperationInCurrentLaboratory(SampleOperation.TAKE_OUT_FROM_LABORATORY)
    }

    $_canViewAllProcess(): boolean {
        return this.$_hasOperationInCurrentLaboratory(SampleOperation.VIEW_ALL_PROCESS)
    }

    /// CAN BE ///

    $_canBeSampleProcessed(sampleStatusCode: Nullable<string>): boolean {
        return !!sampleStatusCode && [
            SAMPLE_STATES.REGISTERED.code,
            SAMPLE_STATES.REPROCESSING_REQUESTED.code,
            SAMPLE_STATES.PREPROCESSED.code,
            SAMPLE_STATES.IN_PROCESSOR.code
        ].includes(sampleStatusCode)
    }
    $_canBeIncidentCreatedToSample(sample: any): boolean {
        return sample?.metadata?.operationGuards.canCreateIncidents
    }
    $_canBeDocumentAttachedToSample(sample: any): boolean {
        return sample?.metadata?.operationGuards.canBeAttached
    }
    $_canBeSampleDeleted(sample: any): boolean {
        return sample?.metadata?.operationGuards.canBeDeleted
    }
    $_canBePortionCreatedToSample(sample: any): boolean {
        return sample?.metadata?.operationGuards.canPortionsBeCreated
    }

    /// DISABLED ///

    $_disabledDepleted(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.depleted
        return isEditionDisabledField || !this.$_canEditSample()
    }
    $_disabledGrossingCoordinator(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.grossingCoordinator
        return isEditionDisabledField || (!this.$_canEditSample() && !sample?.sampleType)
    }
    $_disabledGrossingDelay(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.grossingDelay
        return isEditionDisabledField || (!this.$_canEditSample() && !sample?.sampleType)
    }
    $_disabledGrossingResponsible(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.grossingResponsible
        return isEditionDisabledField || (!this.$_canEditSample() && !sample?.sampleType)
    }
    $_disabledGrossingTechController(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.grossingTechController
        return isEditionDisabledField || (!this.$_canEditSample() && !sample?.sampleType)
    }
    $_disabledGrossingTutor(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.grossingTutor
        return isEditionDisabledField || (!this.$_canEditSample() && !sample?.sampleType)
    }
    $_disabledMostLikelyMorphologicCode(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.mostLikelyMorphologicCode
        return isEditionDisabledField || !this.$_canEditSample()
    }
    $_disabledProcedure(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.procedure
        return isEditionDisabledField || !this.$_canEditSample()
    }
    $_disabledSampleType(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.sampleType
        return isEditionDisabledField || !this.$_canEditSample()
    }
    $_disabledSpecialist(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.specialist
        return isEditionDisabledField || (!this.$_canEditSample() && !sample?.sampleType)
    }
    $_disabledTopographicCode(sample: any): boolean {
        const isEditionDisabledField = sample?.metadata?.editionDisabledFields.topographicCode
        return isEditionDisabledField || !this.$_canEditSample()
    }

    $_disabledDeleteSamples(samples: any[]): boolean {
        return !samples?.length || (samples?.every(sample => !this.$_canBeSampleDeleted(sample)) && !this.$_canDeleteSample())
    }
}
